import { useFormikContext } from 'formik';
import React from 'react';
import { InputLabel, Spacer } from 'src/common';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import MaterialInput from 'src/components/form/MaterialInput';
import { OrganisationSelect, PlanTypeSelect, RestrictAccessSelect } from 'src/components/select';
import { WorkspaceTypeSelect } from 'src/components/select/WorkspaceTypeSelect';
import { WorkspaceFormValues } from './util';
import { useAuth } from 'src/state/auth';
import { useProfileSettingsOptionsQuery } from 'src/graphql/_generated';

interface WorkspaceFormProps {
  disableOrganisationSelect?: boolean;
  organisationDisplayName?: string;
}

export const WorkspaceForm: React.FC<WorkspaceFormProps> = ({
  disableOrganisationSelect = false,
  organisationDisplayName,
}) => {
  const { values, handleChange, handleBlur, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<WorkspaceFormValues>();
  const { data: settingsData } = useProfileSettingsOptionsQuery();

  return (
    <div>
      <InputLabel>WORKSPACE NAME *</InputLabel>
      <AutoGrowingInput
        name="name"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.name}
        placeholder="Workspace name"
        error={touched.name && Boolean(errors.name)}
        helperText={touched.name && errors.name}
        fullWidth
      />
      <Spacer $px={20} />
      <InputLabel>PRIMARY CONTACT *</InputLabel>
      <AutoGrowingInput
        type="email"
        name="primaryContact"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.primaryContact}
        placeholder="Primary contact email address"
        error={touched.primaryContact && Boolean(errors.primaryContact)}
        helperText={touched.primaryContact && errors.primaryContact}
        fullWidth
      />
      <Spacer $px={20} />
      <InputLabel>PARENT ORGANISATION *</InputLabel>
      {disableOrganisationSelect ? (
        <MaterialInput value={organisationDisplayName || ''} disabled fullWidth placeholder="Parent organisation" />
      ) : (
        <OrganisationSelect
          placeholder="Parent organisation"
          value={values.organisationId ?? undefined}
          onChange={(o) => {
            setFieldValue('organisationId', o.value);
            setTimeout(() => setFieldTouched('organisationId', true));
          }}
          onBlur={handleBlur('organisationId')}
          error={touched.organisationId && Boolean(errors.organisationId)}
          helperText={touched.organisationId && errors.organisationId}
          isSearchable
        />
      )}
      <Spacer $px={20} />
      <InputLabel
        tooltip={`<div style="font-size: 12px;padding: 6px 0; max-width: 320px;"><b>Open:</b> (All users within the organisation have automatic access)<br /><br /><b>Restricted:</b> (Users within the organisation must be invited to join the workspace)</div>`}
      >
        WORKSPACE RESTRICTION *
      </InputLabel>
      <RestrictAccessSelect
        value={values.isRestricted}
        onChange={(val) => {
          setFieldValue('isRestricted', val.value);
        }}
        onBlur={handleBlur('isRestricted')}
        error={touched.isRestricted && Boolean(errors.isRestricted)}
        helperText={touched.isRestricted && errors.isRestricted}
      />
      <Spacer $px={20} />
      <InputLabel>ENVIRONMENT TYPE *</InputLabel>
      <WorkspaceTypeSelect
        value={values.plan || ''}
        restrictedTo={settingsData?.profile?.wsSettingsOptions?.plan || []}
        onChange={(o) => {
          setFieldValue('plan', o.value);
          setTimeout(() => setFieldTouched('plan', true));
        }}
        placeholder="Environment type"
        onBlur={handleBlur('plan')}
        error={touched.plan && Boolean(errors.plan)}
        helperText={touched.plan && errors.plan}
      />
      <Spacer $px={20} />
      <InputLabel>CURRENCY</InputLabel>
      <MaterialInput
        id="currency"
        placeholder="Currency"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.currency || ''}
        fullWidth
        error={touched.currency && Boolean(errors.currency)}
        helperText={touched.currency && errors.currency}
      />
      <Spacer $px={20} />
      <InputLabel>PRICING PLAN</InputLabel>
      <PlanTypeSelect
        restrictedTo={settingsData?.profile?.wsSettingsOptions?.planType || []}
        value={values.planType}
        onChange={(val) => setFieldValue('planType', val.value)}
        onBlur={handleBlur('planType')}
        error={touched.planType && Boolean(errors.planType)}
        helperText={touched.planType && errors.planType}
      />
    </div>
  );
};
